// ===========================================
// COMPONENTE: MultiSelectFilter
// ===========================================
// Filtro multi-select reutilizable con dos variantes:
// - "trigger": Boton que abre Popover con checkboxes
// - "embedded": Checkboxes directos sin Popover

import * as React from 'react'
import { ChevronDown, X, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface MultiSelectFilterOption {
  value: string
  label: string
}

interface MultiSelectFilterProps {
  options: MultiSelectFilterOption[]
  value: string[]
  onChange: (value: string[]) => void
  label: string
  variant?: 'trigger' | 'embedded'
  searchable?: boolean
  searchPlaceholder?: string
  triggerClassName?: string
  maxHeight?: string
  /**
   * Qué dice el trigger sin nada seleccionado. Default "Todos", que es la semántica de un filtro
   * (nada tildado = no filtra nada). Cuando el componente se usa para ASIGNAR y no para filtrar,
   * "Todos" dice lo contrario de lo que pasa: de ahí la prop (ORR-364).
   */
  emptyLabel?: string
}

export function MultiSelectFilter({
  options,
  value,
  onChange,
  label,
  variant = 'trigger',
  searchable = false,
  searchPlaceholder,
  triggerClassName,
  maxHeight = 'max-h-60',
  emptyLabel = 'Todos',
}: MultiSelectFilterProps) {
  const [open, setOpen] = React.useState(false)
  const [busqueda, setBusqueda] = React.useState('')
  const scrollRef = React.useRef<HTMLDivElement>(null)

  // Handler manual para wheel events (fix para Radix Popover)
  const handleWheel = React.useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const el = scrollRef.current
    if (!el) return
    e.stopPropagation()
    el.scrollTop += e.deltaY
  }, [])

  const seleccionados = React.useMemo(() => new Set(value), [value])

  const opcionesFiltradas = React.useMemo(() => {
    if (!busqueda) return options
    const query = busqueda.toLowerCase()
    return options.filter((opt) => opt.label.toLowerCase().includes(query))
  }, [options, busqueda])

  const handleToggle = (optionValue: string) => {
    if (seleccionados.has(optionValue)) {
      onChange(value.filter((v) => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  // Trigger label text
  const triggerLabel = React.useMemo(() => {
    if (value.length === 0) return `${label}: ${emptyLabel}`
    if (value.length === 1) {
      const opt = options.find((o) => o.value === value[0])
      return `${label}: ${opt?.label ?? value[0]}`
    }
    return `${label}: ${value.length} sel.`
  }, [value, label, options, emptyLabel])

  // Shared checkbox list
  const checkboxList = (
    <>
      {searchable && (
        <div className={cn(variant === 'trigger' ? 'p-2 border-b' : 'mb-2')}>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder ?? `Buscar ${label.toLowerCase()}...`}
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="h-8 pl-7 text-sm"
            />
          </div>
        </div>
      )}
      <div
        ref={scrollRef}
        onWheel={handleWheel}
        className={cn(maxHeight, 'overflow-y-auto overscroll-contain')}
      >
        <div className={cn(variant === 'trigger' ? 'p-1.5' : '', 'space-y-0.5')}>
          {opcionesFiltradas.map((opt) => (
            <label
              key={opt.value}
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm',
                'hover:bg-muted transition-colors',
                seleccionados.has(opt.value) && 'bg-muted'
              )}
            >
              <Checkbox
                checked={seleccionados.has(opt.value)}
                onCheckedChange={() => handleToggle(opt.value)}
              />
              <span className="truncate">{opt.label}</span>
            </label>
          ))}
          {opcionesFiltradas.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-3">
              Sin resultados
            </p>
          )}
        </div>
      </div>
    </>
  )

  if (variant === 'embedded') {
    return (
      <div className="space-y-1.5">
        <label className="text-xs text-muted-foreground">
          {label} {value.length > 0 && `(${value.length})`}
        </label>
        <div className="border rounded-md">
          {checkboxList}
        </div>
      </div>
    )
  }

  // variant === 'trigger'
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'h-9 justify-between font-normal gap-1',
            value.length > 0 && 'border-primary text-primary',
            triggerClassName
          )}
        >
          <span className="truncate text-sm">{triggerLabel}</span>
          <ChevronDown className={cn(
            'h-3.5 w-3.5 shrink-0 opacity-50 transition-transform',
            open && 'rotate-180'
          )} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        {checkboxList}
        {value.length > 0 && (
          <div className="p-2 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => onChange([])}
            >
              Limpiar selección
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

// Badge para filtros activos con boton de remover
interface FilterBadgeProps {
  prefix: string
  label: string
  onRemove: () => void
}

export function FilterBadge({ prefix, label, onRemove }: FilterBadgeProps) {
  return (
    <Badge variant="secondary" className="gap-1 pr-1 h-6">
      <span className="text-muted-foreground">{prefix}:</span> {label}
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
        aria-label={`Quitar filtro ${prefix}: ${label}`}
      >
        <X className="h-3 w-3" />
      </button>
    </Badge>
  )
}
